import {
  OWNERS_BUYER_INFO_PATH,
  DEV_NOC_BUYER_INFO,
  NOC_UPLOAD_DOC_PATH,
  OWNER_NOC_UPLOAD_DOC_PATH,
  NOC_HOME,
  STRIPE_PAYMENT,
  STRIPE_NOC_SERVICE_CHARGE_ACCT_ID,
  STRIPE_NOC_OWNER_ASSN_ACCT_ID,
  STRIPE_NOC_DEV_FEE_ACCT_ID,
  JOINT_OWNERS_PATH,
} from "@/config/constants";
import { formatAmount } from "@/services/utils/priceFormatter";

export const PayerType = {
  buyer: "buyer",
  seller: "seller",
};

interface NOCPaths {
  [key: number]: {
    stepName?: string;
    path: string;
  };
}

export const OWNER_NOC_MAX_STEPS_LIMIT_CHECK = 5;
export const DEVELOPER_NOC_MAX_STEPS_LIMIT_CHECK = 3;
export const NOC_STEP_IN_PROGRESS = "in-progress";
export const NOC_STEP_NOT_STARTED = "yet-to-start";
export const NOC_STEP_COMPLETED = "completed";
export const OWNER_NOC = "ownerNoc";
export const DEVELOPER_NOC = "developerNoc";
export const NOC_STARTED = "started";

export const OVERALL_STATUS_PENDING = "pending";
export const OVERALL_STATUS_IN_PROGRESS = "in-progress";

export const DEVELOPER_NOC_STEPS = {
  BUYER_INFO: "Provide Buyer information",
  UPLOAD: "Provide Owners association No Objection Certificate",
  PAYMENT: "Pay Fees",
};

export const NORMAL_NOC_STEPS = {
  JOINT_OWNER: "Joint Owner Verification",
  BUYER_INFO: "Provide Buyer information",
  UPLOAD: "Attach your documents",
  BUYER_SUBMIT_DOCUMENTS: "Buyer submits documents",
  ASSIGNMENT_OF_SERVICE_CHARGE: "Service charges transfer",
  PAYMENT: "Pay Fees",
  JOINT_OWNER_APPROVAL: "Obtain all owners approval",
};

export const NOC_PATHS = {
  DEVELOPER_NOC_PATHS: {
    0: {
      stepName: DEVELOPER_NOC_STEPS.BUYER_INFO,
      path: DEV_NOC_BUYER_INFO,
    },
    1: {
      stepName: DEVELOPER_NOC_STEPS.UPLOAD,
      path: NOC_UPLOAD_DOC_PATH,
    },
    2: {
      stepName: DEVELOPER_NOC_STEPS.PAYMENT,
      path: `${STRIPE_PAYMENT}?userType=${PayerType.seller}`,
    },
    3: {
      stepName: "",
      path: NOC_HOME,
    },
    4: {
      stepName: "",
      path: NOC_HOME,
    },
  } as NOCPaths,
  NORMAL_NOC_PATHS: {
    0: {
      stepName: NORMAL_NOC_STEPS.BUYER_INFO,
      path: OWNERS_BUYER_INFO_PATH,
    },
    1: {
      stepName: NORMAL_NOC_STEPS.UPLOAD,
      path: OWNER_NOC_UPLOAD_DOC_PATH,
    },
    2: {
      stepName: "",
      path: NOC_HOME,
    },
    3: {
      stepName: NORMAL_NOC_STEPS.PAYMENT,
      path: `${STRIPE_PAYMENT}?userType=${PayerType.seller}`,
    },
    4: {
      stepName: "",
      path: NOC_HOME,
    },
  } as NOCPaths,
  NORMAL_NOC_PATHS_WITH_JOINT_OWNER: {
    0: {
      stepName: NORMAL_NOC_STEPS.JOINT_OWNER,
      path: JOINT_OWNERS_PATH,
    },
    1: {
      stepName: NORMAL_NOC_STEPS.BUYER_INFO,
      path: OWNERS_BUYER_INFO_PATH,
    },
    2: {
      stepName: NORMAL_NOC_STEPS.UPLOAD,
      path: OWNER_NOC_UPLOAD_DOC_PATH,
    },
    3: {
      stepName: "",
      path: NOC_HOME,
    },
    4: {
      stepName: NORMAL_NOC_STEPS.PAYMENT,
      path: `${STRIPE_PAYMENT}?userType=${PayerType.seller}`,
    },
    5: {
      stepName: "",
      path: NOC_HOME,
    },
  } as NOCPaths,
};

export const Payment_Split_Types = {
  serviceCharge: "service-charge",
  ownerNOC: "owner-noc-fee",
  devNOC: "dev-noc-fee",
};

const FeeType_DomainId_Mapping = {
  [Payment_Split_Types.serviceCharge]: STRIPE_NOC_SERVICE_CHARGE_ACCT_ID,
  [Payment_Split_Types.ownerNOC]: STRIPE_NOC_OWNER_ASSN_ACCT_ID,
  [Payment_Split_Types.devNOC]: STRIPE_NOC_DEV_FEE_ACCT_ID,
};

const CHARGE_TYPE_MAPPING = {
  [Payment_Split_Types.serviceCharge]: "service-charge",
  [Payment_Split_Types.ownerNOC]: "oa-admin-fee",
  [Payment_Split_Types.devNOC]: "dev-admin-fee",
};

export enum PaymentStatus {
  PAID = "paid",
  PENDING = "pending",
}

export function checkCategoryAndPayer(
  array: any,
  category: string,
  payerType: string
) {
  return array.some(
    (item: any) => item.category === category && item.payer === payerType
  );
}

export const checkPendingDuesForUser = (array: any, payerType: string) => {
  return array?.some(
    (item: any) =>
      item.amount > 0 &&
      item.payer === payerType &&
      item.status === PaymentStatus.PENDING
  );
};

export const getTotalDuesForUser = (array: any, payerType: string) => {
  const pendingDues = array?.filter(
    (item: any) =>
      item.amount > 0 &&
      item.payer === payerType &&
      item.status === PaymentStatus.PENDING
  );

  const totalAmount = pendingDues?.reduce(
    (totalAmount: number, item: any) => totalAmount + item.amount,
    0
  );

  return {
    amount: totalAmount,
    formattedAmount: formatAmount(totalAmount),
    breakup: pendingDues,
  };
};

export const mapFeeWithConnectedAccount = (
  pendingDuesBreakup: any,
  unitName: string,
  additionalMetaDataInfo?: any
) => {
  return pendingDuesBreakup?.map((due: any) => ({
    amount: due?.amount,
    connectedAccountId: FeeType_DomainId_Mapping?.[due?.category],
    chargeType: CHARGE_TYPE_MAPPING?.[due?.category],
    description: `${CHARGE_TYPE_MAPPING?.[due?.category]} ${unitName}`,
    splitId: CHARGE_TYPE_MAPPING?.[due?.category],
    ...additionalMetaDataInfo,
  }));
};

export const DEFAULT_TIME_INTERVAL_FOR_DOC_GENERATION= 10000;